import React, { useState, useEffect } from 'react';
import { sendFrontAction } from '../../services/api';
import { normalizePhoneUZ } from '../../utils/validation';
import './ServiceAppointment.css';

// Рабочий график сервиса: 9:00–18:00, шаг 1 час (как во «Визит в шоурум»,
// где прошедшее время в текущем дне тоже блокируется)
const TIME_SLOTS_FOR_CALENDAR = [
    '9:00', '10:00', '11:00', '12:00', '13:00',
    '14:00', '15:00', '16:00', '17:00', '18:00'
];

const MONTHS_RU = [
    'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
    'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
];

const ServiceAppointment = () => {
    const [step, setStep] = useState(1);
    const [selectedServiceCenter, setSelectedServiceCenter] = useState('');
    const [selectedDivision, setSelectedDivision] = useState('');
    const [serviceType, setServiceType] = useState('');
    const [technicians, setTechnicians] = useState([]);
    const [isLoadingTechnicians, setIsLoadingTechnicians] = useState(false);
    // Переменные для шага 4 (Специалист / Дата и время)
    const [selectedSpecialist, setSelectedSpecialist] = useState(null);
    const [selectedTimeSlot, setSelectedTimeSlot] = useState('');
    // --- ДИНАМИЧЕСКИЙ КАЛЕНДАРЬ (как в форме шоурума) ---
    const [today] = useState(() => new Date());
    const [currentMonth, setCurrentMonth] = useState(today.getMonth());
    const [currentYear, setCurrentYear] = useState(today.getFullYear());
    const [selectedDate, setSelectedDate] = useState(null); // теперь объект Date, а не число дня
    // Состояние для управления модальным окном
    const [isModalOpen, setIsModalOpen] = useState(false);
    // Данные финальной формы регистрации
    const [formData, setFormData] = useState({
        clientType: 'Физическое лицо',
        visitedBefore: 'Да',
        vin: '',
        plateNumber: '',
        companyName: '',
        unp: '',
        contactName: '',
        phone: '',
        email: '',
        workDescription: '',
        feedbackMethod: 'Нет'
    });

    const handleBack = () => {
        if (step > 1) {
            // Ступени 4.1 / 4.2 считаем частью "шага 4" при навигации назад
            if (step === 4.2) setStep(4.1);
            else if (step === 4.1) setStep(4);
            else setStep(Math.floor(step) - 1);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    // Загрузка списка мастеров-приемщиков с бэкенда (GET /front/staff?type=technician)
    useEffect(() => {
        const fetchTechnicians = async () => {
            setIsLoadingTechnicians(true);
            try {
                const response = await sendFrontAction('staff', { type: 'technician' });
                if (response && response.success && Array.isArray(response.data)) {
                    setTechnicians(response.data);
                }
            } catch (error) {
                console.error('Ошибка при получении списка мастеров:', error);
            } finally {
                setIsLoadingTechnicians(false);
            }
        };
        fetchTechnicians();
    }, []);

    // --- ВЫБОР МАСТЕРА (Шаг 4.1) ---
    // Закрепить мастера и перейти к обычному календарю (Шаг 4.2) — точное время
    // мастер потом согласует с клиентом по телефону
    const handleSelectSpecialist = (spec) => {
        setSelectedSpecialist(spec);
        setSelectedTimeSlot('');
        setSelectedDate(null);
        setStep(4.2);
    };

    const handleContinueWithoutSpecialist = () => {
        setSelectedSpecialist(null);
        setSelectedTimeSlot('');
        setSelectedDate(null);
        setStep(4.2);
    };

    // --- ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ КАЛЕНДАРЯ ---
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    const firstDayIndex = (new Date(currentYear, currentMonth, 1).getDay() + 6) % 7;

    const handlePrevMonth = () => {
        if (currentYear === today.getFullYear() && currentMonth === today.getMonth()) return;
        if (currentMonth === 0) {
            setCurrentMonth(11);
            setCurrentYear(prev => prev - 1);
        } else {
            setCurrentMonth(prev => prev - 1);
        }
    };
    const handleNextMonth = () => {
        if (currentMonth === 11) {
            setCurrentMonth(0);
            setCurrentYear(prev => prev + 1);
        } else {
            setCurrentMonth(prev => prev + 1);
        }
    };
    const handleDateSelect = (day) => {
        const clickedDate = new Date(currentYear, currentMonth, day);
        const tempToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        if (clickedDate < tempToday) return;
        setSelectedDate(clickedDate);
        setSelectedTimeSlot('');
    };
    const isToday = (day) => {
        return today.getDate() === day && today.getMonth() === currentMonth && today.getFullYear() === currentYear;
    };
    const isPast = (day) => {
        const dateToCheck = new Date(currentYear, currentMonth, day);
        const tempToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        return dateToCheck < tempToday;
    };
    const isSelected = (day) => {
        if (!selectedDate) return false;
        return selectedDate.getDate() === day &&
               selectedDate.getMonth() === currentMonth &&
               selectedDate.getFullYear() === currentYear;
    };
    const formatDateString = (date) => {
        if (!date) return '';
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        return `${day}.${month}.${date.getFullYear()}`;
    };
    const weekdayRu = (date) => {
        if (!date) return '';
        const name = date.toLocaleDateString('ru-RU', { weekday: 'long' });
        return name.charAt(0).toUpperCase() + name.slice(1);
    };
    // Блокирует уже прошедшее время, если выбран сегодняшний день
    // (та же логика, что и в форме "Визит в шоурум")
    const isTimeSlotDisabled = (time) => {
        if (!selectedDate) return true;
        const isSelectedDayToday =
            selectedDate.getDate() === today.getDate() &&
            selectedDate.getMonth() === today.getMonth() &&
            selectedDate.getFullYear() === today.getFullYear();
        if (isSelectedDayToday) {
            const now = new Date();
            const [slotHours, slotMinutes] = time.split(':').map(Number);
            const currentTotalMinutes = now.getHours() * 60 + now.getMinutes();
            const slotTotalMinutes = slotHours * 60 + (slotMinutes || 0);
            return slotTotalMinutes <= currentTotalMinutes;
        }
        return false;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const formattedDate = selectedDate
                ? `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`
                : null;
            const scheduledAt = formattedDate && selectedTimeSlot ? `${formattedDate} ${selectedTimeSlot}:00` : null;
            const payload = {
                client_name: formData.contactName,
                client_phone: normalizePhoneUZ(formData.phone),
                vin: formData.vin,
                plate_number: formData.plateNumber,
                client_type: formData.clientType,
                visited_before: formData.visitedBefore,
                company_name: formData.companyName,
                unp: formData.unp,
                email: formData.email,
                service_type: serviceType,
                description: formData.workDescription,
                scheduled_at: scheduledAt,
                technician_id: selectedSpecialist?.id || null,
                feedback_method: formData.feedbackMethod,
            };
            const response = await sendFrontAction('service', payload);
            if (response && response.success) {
                setIsModalOpen(true);
            } else {
                alert(response?.message || 'Произошла ошибка при сохранении записи');
            }
        } catch (error) {
            console.error('Ошибка при создании заявки на сервис:', error);
            alert('Не удалось отправить заявку. Пожалуйста, попробуйте позже.');
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        // Сброс на первый шаг при закрытии модального окна
        setStep(1);
    };

    return (
        <div className="srv-container">
            <h1 className="srv-main-title">ОНЛАЙН-ЗАПИСЬ НА СЕРВИС</h1>

            {step > 1 && (
                <div className="srv-navigation">
                    <button className="srv-back-button" onClick={handleBack}>
                        <span>‹</span> Назад
                    </button>
                    <div className="srv-breadcrumbs">
                        <span>PREMIUM AUTO</span>
                        {step >= 3 && <span> / {selectedDivision}</span>}
                        {step >= 4 && <span> / {serviceType}</span>}
                        {step >= 4.2 && selectedSpecialist && <span> / {selectedSpecialist.name}</span>}
                    </div>
                </div>
            )}

            {/* Скрин 1: Выбор сервиса */}
            {step === 1 && (
                <div className="srv-step-wrapper">
                    <p className="srv-step-label">Выберите сервис</p>
                    <div className="srv-dealer-card" onClick={() => { setSelectedServiceCenter('PREMIUM AUTO'); setStep(2); }}>
                        <div className="srv-dealer-header">
                            <span className="srv-logo-text">PREMIUM AUTO</span>
                        </div>
                        <div className="srv-dealer-footer">
                            <span>г. Ташкент, Мирабадский район, ул. Авлио Ота 128</span>
                            <span className="srv-map-link">На карте</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Скрин 2: Выбор подразделения */}
            {step === 2 && (
                <div className="srv-step-wrapper">
                    <p className="srv-step-label">Выберите подразделение</p>
                    <div className="srv-list-menu">
                        <div className="srv-menu-btn" onClick={() => { setSelectedDivision('Автомобили BMW'); setStep(3); }}>
                            <div className="srv-icon-placeholder bmw-logo-small"></div>
                            <span>Автомобили BMW</span>
                        </div>
                        <div className="srv-menu-btn" onClick={() => { setSelectedDivision('Мотоциклы BMW Motorrad'); setStep(3); }}>
                            <div className="srv-icon-placeholder motorrad-logo-small"></div>
                            <span>Мотоциклы BMW Motorrad</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Скрин 3: Выбрать тип обслуживания */}
            {step === 3 && (
                <div className="srv-step-wrapper">
                    <p className="srv-step-label">Выберите причину обращения</p>
                    <div className="srv-list-menu">
                        <div className="srv-menu-btn" onClick={() => { setServiceType('Техническое обслуживание'); setStep(4); }}>
                            <div className="srv-icon-placeholder tool-icon"></div>
                            <span>Техническое обслуживание</span>
                        </div>
                        <div className="srv-menu-btn" onClick={() => { setServiceType('Техническое обслуживание по гарантии'); setStep(4); }}>
                            <div className="srv-icon-placeholder shield-icon"></div>
                            <span>Техническое обслуживание по гарантии</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Скрин 4: Выбор маршрута (Специалист или Дата) */}
            {step === 4 && (
                <div className="srv-step-wrapper">
                    <p className="srv-step-label">Выберите причину обращения</p>
                    <div className="srv-routing-options">
                        <div className="srv-route-card active" onClick={() => setStep(4.1)}>
                            <div className="srv-route-icon user-circle"></div>
                            <span>Выберите сотрудника</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Скрин 5: Выбор специалиста (мастера-приемщика) */}
            {step === 4.1 && (
                <div className="srv-step-wrapper animate-fade-in">
                    <p className="srv-step-label">Выберите мастера-приемщика</p>
                    {isLoadingTechnicians ? (
                        <p>Загрузка...</p>
                    ) : technicians.length === 0 ? (
                        <div className="srv-action-placement">
                            <p className="no-cars-message">Нет доступных мастеров. Вы можете выбрать дату и время без привязки к мастеру.</p>
                            <button className="srv-primary-blue-btn" onClick={handleContinueWithoutSpecialist}>
                                Выбрать дату и время
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="srv-specialists-list">
                                {technicians.map((spec) => (
                                    <div
                                        key={spec.id}
                                        className={`srv-spec-card ${selectedSpecialist?.id === spec.id ? 'selected' : ''}`}
                                        onClick={() => handleSelectSpecialist(spec)}
                                    >
                                        <div className="srv-spec-header-row">
                                            <div className="srv-spec-avatar">
                                                {spec.avatar && <img src={spec.avatar} alt={spec.name} />}
                                            </div>
                                            <span className="srv-spec-name">{spec.name}</span>
                                            <span className="srv-spec-chevron">›</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="srv-action-placement">
                                <button
                                    type="button"
                                    className="srv-secondary-link-btn"
                                    onClick={handleContinueWithoutSpecialist}
                                >
                                    Продолжить без выбора мастера
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* Скрин 7: Календарь и слоты времени */}
            {step === 4.2 && (
                <div className="srv-step-wrapper animate-fade-in">
                    {selectedSpecialist && (
                        <div className="srv-attached-spec-badge">
                            <div className="srv-spec-avatar micro">
                                {selectedSpecialist.avatar && <img src={selectedSpecialist.avatar} alt={selectedSpecialist.name} />}
                            </div>
                            <span>{selectedSpecialist.name}</span>
                        </div>
                    )}
                    <div className="srv-calendar-navigation">
                        <button
                            type="button"
                            className={`srv-month-nav-btn ${currentYear === today.getFullYear() && currentMonth === today.getMonth() ? 'disabled' : ''}`}
                            onClick={handlePrevMonth}
                            disabled={currentYear === today.getFullYear() && currentMonth === today.getMonth()}
                        >
                            ‹
                        </button>
                        <h2 className="srv-calendar-month">{MONTHS_RU[currentMonth]} {currentYear}</h2>
                        <button type="button" className="srv-month-nav-btn" onClick={handleNextMonth}>›</button>
                    </div>
                    <div className="srv-calendar-table">
                        {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map(d => <div key={d} className="srv-th">{d}</div>)}
                        {Array.from({ length: firstDayIndex }).map((_, idx) => (
                            <div key={`empty-${idx}`} className="srv-td empty"></div>
                        ))}
                        {Array.from({ length: daysInMonth }).map((_, i) => {
                            const day = i + 1;
                            const isPastDay = isPast(day);
                            const isSelectedDay = isSelected(day);
                            return (
                                <div
                                    key={day}
                                    className={`srv-td ${isSelectedDay ? 'active-day' : ''} ${isPastDay ? 'disabled-day' : ''} ${isToday(day) ? 'current' : ''}`}
                                    onClick={() => { if (!isPastDay) handleDateSelect(day); }}
                                >
                                    {day}
                                </div>
                            );
                        })}
                    </div>
                    {selectedDate && (
                        <div className="srv-time-slots-section">
                            <p className="srv-step-label">Выберите время</p>
                            <div className="srv-time-slots-grid">
                                {TIME_SLOTS_FOR_CALENDAR.map(slot => {
                                    const isDisabled = isTimeSlotDisabled(slot);
                                    return (
                                        <button
                                            key={slot}
                                            type="button"
                                            disabled={isDisabled}
                                            className={`srv-time-grid-btn ${selectedTimeSlot === slot ? 'active' : ''} ${isDisabled ? 'time-disabled' : ''}`}
                                            onClick={() => setSelectedTimeSlot(slot)}
                                        >
                                            {slot}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    {selectedTimeSlot && (
                        <button className="srv-primary-blue-btn margin-top-all" onClick={() => setStep(5)}>
                            Далее ➔
                        </button>
                    )}
                </div>
            )}

            {/* Скрин 6 и 8: Форма регистрации */}
            {step === 5 && (
                <form onSubmit={handleSubmit} className="srv-step-wrapper srv-registration-form animate-fade-in">
                    <div className="srv-form-group">
                        <label>Субъект</label>
                        <div className="srv-radio-flex">
                            {['Физическое лицо', 'Юридическое лицо'].map(t => (
                                <label key={t} className="srv-radio-item">
                                    <input type="radio" name="clientType" value={t} checked={formData.clientType === t} onChange={handleInputChange} />
                                    <span>{t}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                    <div className="srv-form-group">
                        <label>Обслуживались ранее в PREMIUM AUTO?</label>
                        <div className="srv-radio-flex">
                            {['Да', 'Нет'].map(o => (
                                <label key={o} className="srv-radio-item">
                                    <input type="radio" name="visitedBefore" value={o} checked={formData.visitedBefore === o} onChange={handleInputChange} />
                                    <span>{o}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                    <div className="srv-form-group">
                        <label>VIN *</label>
                        <input type="text" name="vin" placeholder="1234567890ABCDEFG" required value={formData.vin} onChange={handleInputChange} />
                    </div>
                    <div className="srv-form-group">
                        <label>Гос. номер *</label>
                        <input type="text" name="plateNumber" placeholder="01 A 777 AA" required value={formData.plateNumber} onChange={handleInputChange} />
                    </div>
                    {formData.clientType === 'Юридическое лицо' && (
                        <>
                            <div className="srv-form-group">
                                <label>Наименование *</label>
                                <input type="text" name="companyName" required value={formData.companyName} onChange={handleInputChange} />
                            </div>
                            <div className="srv-form-group">
                                <label>ИНН / УНП *</label>
                                <input type="text" name="unp" required value={formData.unp} onChange={handleInputChange} />
                            </div>
                        </>
                    )}
                    <div className="srv-form-group">
                        <label>Имя контактного лица *</label>
                        <input type="text" name="contactName" required value={formData.contactName} onChange={handleInputChange} />
                    </div>
                    <div className="srv-form-group">
                        <label>Телефон *</label>
                        <div className="srv-uzb-phone-wrapper">
                            <span className="srv-flag-icon">🇺🇿</span>
                            <span className="srv-phone-prefix">+998</span>
                            <input
                                type="tel"
                                name="phone"
                                placeholder="(90) 123-45-67"
                                required
                                value={formData.phone}
                                onChange={handleInputChange}
                            />
                        </div>
                    </div>
                    <div className="srv-form-group">
                        <label>Email</label>
                        <input type="email" name="email" value={formData.email} onChange={handleInputChange} />
                    </div>
                    <div className="srv-form-group">
                        <label>Укажите перечень работ *</label>
                        <textarea name="workDescription" rows="4" required value={formData.workDescription} onChange={handleInputChange}></textarea>
                    </div>
                    {/* Сводка записи */}
                    <div className="srv-appointment-summary-widget">
                        <h3 className="srv-widget-date-title">
                            Ваша запись на {selectedDate ? `${formatDateString(selectedDate)} (${weekdayRu(selectedDate)})` : '—'}, {selectedTimeSlot}
                        </h3>
                        {selectedSpecialist && (
                            <div className="srv-widget-master-info">
                                <div className="srv-widget-avatar">
                                    {selectedSpecialist.avatar && <img src={selectedSpecialist.avatar} alt={selectedSpecialist.name} />}
                                </div>
                                <div>
                                    <p className="srv-widget-name">{selectedSpecialist.name}</p>
                                    <p className="srv-widget-role">{selectedSpecialist.role}</p>
                                    <p className="srv-widget-dep">{serviceType} [{selectedDivision}]</p>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="srv-form-group">
                        <label className="srv-heading-label">Если у вас остались вопросы, выберите канал для связи</label>
                        <div className="srv-radio-inline-row">
                            {['Нет', 'Да (телефон)', 'Viber', 'Telegram', 'Email'].map(ch => (
                                <label key={ch} className="srv-radio-item">
                                    <input type="radio" name="feedbackMethod" value={ch} checked={formData.feedbackMethod === ch} onChange={handleInputChange} />
                                    <span>{ch}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                    <p className="srv-form-required-text">* Обязательные для заполнения поля</p>
                    <button type="submit" className="srv-primary-blue-btn srv-form-submit">
                        Отправить
                    </button>
                </form>
            )}

            {/* --- МОДАЛЬНОЕ ОКНО ПОДТВЕРЖДЕНИЯ --- */}
            {isModalOpen && (
                <div className="modal-overlay" onClick={handleCloseModal}>
                    <div className="modal-container" onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close-btn" onClick={handleCloseModal}>&times;</button>
                        <div className="modal-header">
                            <div className="modal-icon-success">✓</div>
                            <h2>Заявка успешно отправлена!</h2>
                        </div>
                        <div className="modal-body">
                            <div className="modal-info-row">
                                <span>Сервис:</span>
                                <strong>{selectedServiceCenter} ({selectedDivision})</strong>
                            </div>
                            <div className="modal-info-row">
                                <span>Услуга:</span>
                                <strong>{serviceType}</strong>
                            </div>
                            <div className="modal-info-row">
                                <span>Мастер:</span>
                                <strong>{selectedSpecialist?.name || 'Не выбран'}</strong>
                            </div>
                            <div className="modal-info-row">
                                <span>Дата и время:</span>
                                <strong>{selectedDate ? formatDateString(selectedDate) : '—'} в {selectedTimeSlot}</strong>
                            </div>
                            <div className="modal-info-row">
                                <span>Автомобиль (VIN):</span>
                                <strong>{formData.vin}</strong>
                            </div>
                            <div className="modal-info-row">
                                <span>Гос. номер:</span>
                                <strong>{formData.plateNumber}</strong>
                            </div>
                            <div className="modal-info-row">
                                <span>Клиент:</span>
                                <strong>{formData.contactName}</strong>
                            </div>
                            <div className="modal-info-row">
                                <span>Телефон:</span>
                                <strong>+998 {formData.phone}</strong>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="modal-primary-btn" onClick={handleCloseModal}>
                                Отлично
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ServiceAppointment;